/**
 * GuideCreationDialog
 *
 * Consumer-facing progress dialog for guide generation.
 * Minimal, modern design — shows one active step at a time with
 * a progress indicator, detail text, and cost summary.
 *
 * Subscribes directly to Firestore (same data as GenerationProgress)
 * but presents it in a completely different, simpler UI.
 */

import React, {useState, useCallback, useEffect, useRef} from 'react';
import {
  Dialog,
  Box,
  Typography,
  Button,
  CircularProgress,
  Fade,
  keyframes,
} from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import {doc, collection, query, orderBy, onSnapshot, Timestamp} from 'firebase/firestore';
import {db} from '../config/firebase';
import {useNavigate} from 'react-router-dom';
import {useToast} from '../contexts/ToastContext';

// ─── Stage labels for GUIDE jobs (consumer-friendly wording) ───
const GUIDE_STAGES = [
  {key: 'QUEUED', label: 'Starting up'},
  {key: 'VALIDATING_INPUTS', label: 'Reading your prompt'},
  {key: 'UPLOADING_AND_PARSING_FILES', label: 'Processing attachments'},
  {key: 'GENERATING_DRAFT_STRUCTURE', label: 'Building your guide'},
  {key: 'FILLING_STEP_DETAILS', label: 'Adding details'},
  {key: 'NORMALIZING_AND_VALIDATING', label: 'Polishing'},
  {key: 'SAVING_DRAFT', label: 'Saving'},
  {key: 'FINALIZING', label: 'Wrapping up'},
];

function stageIndex(key: string): number {
  const idx = GUIDE_STAGES.findIndex(s => s.key === key);
  return idx >= 0 ? idx : 0;
}

function stageLabel(key: string): string {
  return GUIDE_STAGES.find(s => s.key === key)?.label ?? 'Working';
}

function formatTokens(count: number): string {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 1000) return `${(count / 1000).toFixed(1)}k`;
  return String(count);
}

function formatCost(cost: number): string {
  return cost < 0.01 ? `$${cost.toFixed(4)}` : `$${cost.toFixed(2)}`;
}

// ─── Animations ───
const pulseRing = keyframes`
  0%   { transform: scale(1);   opacity: 0.25; }
  50%  { transform: scale(1.6); opacity: 0; }
  100% { transform: scale(1.6); opacity: 0; }
`;

const checkPop = keyframes`
  0%   { transform: scale(0); opacity: 0; }
  60%  { transform: scale(1.2); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`;

// ─── Component ───
interface GuideCreationDialogProps {
  open: boolean;
  jobId: string | null;
  onClose: () => void;
}

interface JobState {
  status: string;
  currentStage: string;
  resultRef?: string | null;
  errorMessage?: string | null;
  inputSummary?: string | null;
  resultData?: {
    tokenUsage?: {input: number; output: number; total: number};
    costEstimate?: {totalCost: number} | null;
  } | null;
}

const AUTO_NAVIGATE_MS = 2500;

const GuideCreationDialog: React.FC<GuideCreationDialogProps> = ({open, jobId, onClose}) => {
  const navigate = useNavigate();
  const {showToast} = useToast();
  const [job, setJob] = useState<JobState | null>(null);
  const [latestMessage, setLatestMessage] = useState<string | null>(null);
  const [latestTokens, setLatestTokens] = useState<number | null>(null);
  const [completedRef, setCompletedRef] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const startTimeRef = useRef(Date.now());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Reset when dialog opens with a new job
  useEffect(() => {
    if (open && jobId) {
      setJob(null);
      setLatestMessage(null);
      setLatestTokens(null);
      setCompletedRef(null);
      setElapsed(0);
      startTimeRef.current = Date.now();
      if (timerRef.current) clearTimeout(timerRef.current);
    }
  }, [open, jobId]);

  // Subscribe to job document
  useEffect(() => {
    if (!open || !jobId) return;
    const docRef = doc(db, 'generation_jobs', jobId);
    const unsub = onSnapshot(docRef, (snap) => {
      if (!snap.exists()) return;
      const data = snap.data() as JobState;
      setJob(data);

      if (data.status === 'SUCCEEDED' && data.resultRef && !completedRef) {
        setCompletedRef(data.resultRef);
        timerRef.current = setTimeout(() => {
          onClose();
          navigate(`/org/guides/${data.resultRef}/edit`);
        }, AUTO_NAVIGATE_MS);
      }
    });
    return () => unsub();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, jobId]);

  // Subscribe to events for sub-messages
  useEffect(() => {
    if (!open || !jobId) return;
    const eventsRef = collection(db, 'generation_jobs', jobId, 'events');
    const q = query(eventsRef, orderBy('timestamp', 'asc'));
    const unsub = onSnapshot(q, (snap) => {
      let msg: string | null = null;
      let tokens: number | null = null;
      snap.docs.forEach((d) => {
        const ev = d.data();
        if (ev.message) msg = ev.message;
        if (ev.metadata?.tokens != null) tokens = ev.metadata.tokens;
      });
      setLatestMessage(msg);
      setLatestTokens(tokens);
    });
    return () => unsub();
  }, [open, jobId]);

  // Elapsed time ticker
  const isRunning = job && (job.status === 'QUEUED' || job.status === 'RUNNING');
  useEffect(() => {
    if (isRunning) {
      tickRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 1000);
    } else if (tickRef.current) {
      clearInterval(tickRef.current);
    }
    return () => { if (tickRef.current) clearInterval(tickRef.current); };
  }, [isRunning]);

  // Cleanup on unmount
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const handleOpenGuide = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (completedRef) {
      onClose();
      navigate(`/org/guides/${completedRef}/edit`);
    }
  }, [completedRef, navigate, onClose]);

  // ─── Derived state ───
  const isSucceeded = job?.status === 'SUCCEEDED';
  const isFailed = job?.status === 'FAILED' || job?.status === 'CANCELED';
  const currentStage = job?.currentStage ?? 'QUEUED';
  const progress = isSucceeded
    ? 1
    : (stageIndex(currentStage) + 0.5) / GUIDE_STAGES.length;

  const totalTokens = job?.resultData?.tokenUsage?.total ?? latestTokens;

  // Cost: prefer resultData (final), fall back to parsing inputSummary during generation
  let totalCost = job?.resultData?.costEstimate?.totalCost ?? null;
  if (totalCost == null && job?.inputSummary) {
    const costMatch = job.inputSummary.match(/~\$([0-9.]+)/);
    if (costMatch) totalCost = parseFloat(costMatch[1]);
  }

  const elapsedStr = elapsed < 60
    ? `${elapsed}s`
    : `${Math.floor(elapsed / 60)}m ${(elapsed % 60).toString().padStart(2, '0')}s`;

  return (
    <Dialog
      open={open && !!jobId}
      onClose={completedRef ? handleOpenGuide : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
          background: 'linear-gradient(180deg, #FAFBFF 0%, #FFFFFF 40%)',
        },
      }}
    >
      <Box sx={{px: 4, pt: 5, pb: 4, textAlign: 'center'}}>
        {/* ──── Indicator ──── */}
        <Box sx={{position: 'relative', width: 72, height: 72, mx: 'auto', mb: 3}}>
          {isSucceeded ? (
            // Success checkmark
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                bgcolor: '#10B981',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: `${checkPop} 0.4s ease-out`,
              }}
            >
              <CheckRoundedIcon sx={{fontSize: 36, color: '#fff'}} />
            </Box>
          ) : isFailed ? (
            // Error icon
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                bgcolor: '#FEE2E2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ErrorOutlineRoundedIcon sx={{fontSize: 36, color: '#EF4444'}} />
            </Box>
          ) : (
            // Running — spinner with pulse ring
            <>
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: '50%',
                  border: '2px solid',
                  borderColor: 'primary.main',
                  animation: `${pulseRing} 2s ease-out infinite`,
                }}
              />
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CircularProgress
                  variant="determinate"
                  value={progress * 100}
                  size={64}
                  thickness={3}
                  sx={{
                    color: 'primary.main',
                    '& .MuiCircularProgress-circle': {
                      transition: 'stroke-dashoffset 0.6s ease',
                    },
                  }}
                />
                <Typography
                  sx={{
                    position: 'absolute',
                    fontSize: 13,
                    fontWeight: 600,
                    color: 'primary.main',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {elapsedStr}
                </Typography>
              </Box>
            </>
          )}
        </Box>

        {/* ──── Headline ──── */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            fontSize: 20,
            mb: 0.5,
            color: isFailed ? '#991B1B' : '#111827',
          }}
        >
          {isSucceeded
            ? 'Your guide is ready'
            : isFailed
              ? 'Something went wrong'
              : 'Creating your guide'}
        </Typography>

        {/* ──── Current step label ──── */}
        {!isSucceeded && !isFailed && (
          <Fade in key={currentStage}>
            <Typography sx={{fontSize: 14, color: '#6B7280', mb: 1}}>
              {stageLabel(currentStage)}
            </Typography>
          </Fade>
        )}

        {/* ──── Step dots ──── */}
        {!isSucceeded && !isFailed && (
          <Box sx={{display: 'flex', justifyContent: 'center', gap: 0.75, mb: 2}}>
            {GUIDE_STAGES.map((s, i) => {
              const idx = stageIndex(currentStage);
              const isDone = i < idx;
              const isActive = i === idx;
              return (
                <Box
                  key={s.key}
                  sx={{
                    width: isActive ? 20 : 6,
                    height: 6,
                    borderRadius: 3,
                    bgcolor: isDone
                      ? '#10B981'
                      : isActive
                        ? 'primary.main'
                        : '#E5E7EB',
                    transition: 'all 0.3s ease',
                  }}
                />
              );
            })}
          </Box>
        )}

        {/* ──── Detail text ──── */}
        {!isSucceeded && !isFailed && latestMessage && (
          <Fade in key={latestMessage}>
            <Typography
              sx={{
                fontSize: 12,
                color: '#9CA3AF',
                fontStyle: 'italic',
                mb: 2,
                minHeight: 18,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {latestMessage}
            </Typography>
          </Fade>
        )}

        {/* ──── Error message ──── */}
        {isFailed && (
          <Typography sx={{fontSize: 13, color: '#6B7280', mb: 2}}>
            {job?.errorMessage || 'The generation job failed. Please try again.'}
          </Typography>
        )}

        {/* ──── Token / cost pill ──── */}
        {totalTokens != null && totalTokens > 0 && (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.5,
              py: 0.5,
              borderRadius: 2,
              bgcolor: '#F3F4F6',
              mb: 3,
            }}
          >
            <Typography sx={{fontSize: 12, fontWeight: 500, color: '#6B7280', fontVariantNumeric: 'tabular-nums'}}>
              {formatTokens(totalTokens)} tokens
            </Typography>
            {totalCost != null && totalCost > 0 && (
              <>
                <Box sx={{width: 3, height: 3, borderRadius: '50%', bgcolor: '#D1D5DB'}} />
                <Typography sx={{fontSize: 12, fontWeight: 500, color: '#6B7280'}}>
                  ~{formatCost(totalCost)}
                </Typography>
              </>
            )}
          </Box>
        )}

        {/* ──── Actions ──── */}
        <Box sx={{mt: isSucceeded ? 2 : 1}}>
          {isSucceeded ? (
            <Button
              variant="contained"
              onClick={handleOpenGuide}
              disableElevation
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                px: 4,
                py: 1,
              }}
            >
              Open guide
            </Button>
          ) : isFailed ? (
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{textTransform: 'none', borderRadius: 2}}
            >
              Close
            </Button>
          ) : (
            <Button
              onClick={() => {
                showToast({
                  message: 'Your guide is generating in the background',
                  severity: 'info',
                  action: {label: 'View job', to: `/org/jobs/${jobId}`},
                });
                onClose();
              }}
              size="small"
              sx={{
                textTransform: 'none',
                color: '#9CA3AF',
                fontSize: 13,
                '&:hover': {color: '#6B7280', bgcolor: 'transparent'},
              }}
            >
              Run in background
            </Button>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

export default GuideCreationDialog;
